import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { authControllerRefreshToken } from '~/api';
import { store } from '~/store';
import { signOut } from '~/store/admin/actions';

export const API_HOST = import.meta.env.VITE_API_URL;

export async function heyApiQuery<T>(
  promiseFn: () => Promise<{
    data?: T;
    error?: unknown;
    response: Response;
  }>
): Promise<{ data: T } | { error: FetchBaseQueryError }> {
  let result = await promiseFn();

  if (result.response.status === 401 && !result.response.url.includes('/auth/refresh')) {
    try {
      const refresh = await heyApiQuery(() => authControllerRefreshToken());

      if ('data' in refresh) {
        result = await promiseFn();
      } else {
        void store.dispatch(signOut());
        return { error: refresh.error };
      }
    } catch (e) {
      return { error: { status: 401, data: e } };
    }
  }

  if (result.error) {
    return {
      error: {
        status: result.response.status,
        data: result.error,
      },
    };
  }

  return { data: result.data as T };
}

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const hasKey = <K extends string>(obj: Record<string, unknown>, key: K): obj is Record<K, unknown> => key in obj;

export function getErrorMessage(err: unknown): string {
  const defaultMsg = 'general.generalErrorMsg';

  if (!err) return defaultMsg;

  if (typeof err === 'string') return err;

  if (err instanceof Error) return err.message;

  if (!isObject(err)) return defaultMsg;

  if (hasKey(err, 'data') && isObject(err.data)) {
    const msg = err.data.message;
    if (typeof msg === 'string') return msg;

    if (Array.isArray(msg)) return msg.join(', ');
  }

  if (hasKey(err, 'message') && typeof err.message === 'string') {
    return err.message;
  }

  if (hasKey(err, 'error') && typeof err.error === 'string') {
    return err.error;
  }

  if (hasKey(err, 'data') && typeof err.data === 'string') {
    return err.data;
  }

  return defaultMsg;
}
